import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsInvestigationAnalysis() {
  return (
    <DocsLayout>
      <DocsContent
        title="Investigation Analysis"
        description="Understand the algorithms and scoring methodology behind the Investigation Overview — clusters, bridges, entity ranking, and the investigation score."
        previousPage={{ title: 'Graph Explorer', href: '/docs/graph-explorer' }}
        nextPage={{ title: 'Timeline View', href: '/docs/timeline-view' }}
      >
        <H2 id="investigation-score">Investigation Score</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The <strong className="text-foreground">Investigation Score</strong> is a weighted
          composite that ranks how significant an entity is to your investigation. It ranges from 0
          to 100 and is calculated from four factors:
        </p>

        <Card className="my-6 rounded-lg bg-card/50 p-5">
          <div className="space-y-3 text-sm text-foreground">
            <div className="flex items-center justify-between gap-3">
              <span>
                <strong className="text-foreground">Target Proximity</strong> — how closely linked
                to suspect/target entities
              </span>
              <span className="font-mono text-amber-600 dark:text-amber-400">30%</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>
                <strong className="text-foreground">Evidence Diversity</strong> — variety of
                relationship types
              </span>
              <span className="font-mono text-amber-600 dark:text-amber-400">25%</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>
                <strong className="text-foreground">Source Diversity</strong> — number of distinct
                source documents
              </span>
              <span className="font-mono text-amber-600 dark:text-amber-400">25%</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>
                <strong className="text-foreground">Connections</strong> — total relationship count
                (degree centrality)
              </span>
              <span className="font-mono text-amber-600 dark:text-amber-400">20%</span>
            </div>
          </div>
        </Card>

        <H3 id="score-thresholds">Score Thresholds</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          Scores are color-coded in the UI to help you quickly prioritize:
        </p>

        <div className="grid grid-cols-3 gap-3 my-6">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-center">
            <span className="text-2xl font-mono font-bold text-red-500">70+</span>
            <p className="text-xs text-muted-foreground mt-1">
              High — likely key to the investigation
            </p>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-center">
            <span className="text-2xl font-mono font-bold text-amber-600 dark:text-amber-500">
              40–69
            </span>
            <p className="text-xs text-muted-foreground mt-1">
              Notable — warrants closer inspection
            </p>
          </div>
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-center">
            <span className="text-2xl font-mono font-bold text-blue-500">15–39</span>
            <p className="text-xs text-muted-foreground mt-1">Low — peripheral or supporting</p>
          </div>
        </div>

        <DocsCallout variant="tip" title="Possible-Match Bonus">
          Entities flagged as "possible match" against a target receive a bonus that boosts their
          score. This ensures near-matches surface even if their raw connection metrics are low.
        </DocsCallout>

        <H2 id="graph-clusters">Graph Clusters</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Clusters are discovered using the{' '}
          <strong className="text-foreground">Louvain community detection</strong> algorithm from
          the Neo4j Graph Data Science library. Louvain maximises modularity — it groups nodes that
          are more densely connected to each other than to the rest of the graph, producing tight
          communities rather than whatever happens to be transitively reachable.
        </p>

        <p className="text-foreground mb-4 leading-relaxed">
          Unlike connected-components algorithms (such as WCC), Louvain will{' '}
          <strong className="text-foreground">
            split two groups that are linked by a single weak edge
          </strong>{' '}
          into separate clusters. A lone courier shared between two rings will not collapse the
          rings into one blob — each ring keeps its own cluster and the courier surfaces as a bridge
          (see below).
        </p>

        <H3 id="what-a-cluster-means">What a Cluster Means</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          A cluster is a densely interconnected community of entities — people, organisations,
          locations, and so on. Two entities in different clusters may still share an edge; that
          edge just wasn't strong enough to merge the communities, and it makes one of the endpoints
          a <em>bridge</em>. Structurally, the two entities belong to different neighbourhoods of
          the case.
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Multiple clusters</strong> often indicate distinct
              sub-investigations or unrelated groups within your case data
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">A single dominant cluster</strong> suggests a
              highly interconnected case where most entities share dense relationships
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Small clusters</strong> (single nodes) are
              filtered out because they are almost always document artefacts or incomplete
              extractions
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          Each entity node is tagged with its{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">component_id</code> in Neo4j, so
          the cluster assignment is available to every downstream view — graph explorer, pattern
          search, and pattern filters all honour the same cluster labels.
        </p>

        <DocsCallout variant="info" title="Minimum Cluster Size">
          Only clusters with at least <strong className="text-foreground">2</strong> entities are
          surfaced. Single-node components are usually document artefacts or incomplete extractions
          and are filtered out to reduce noise. The threshold is a hardcoded constant in the
          backend, not a user-tunable setting.
        </DocsCallout>

        <H2 id="cross-cluster-bridges">Cross-Cluster Bridges</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          A <strong className="text-foreground">bridge</strong> is an entity that has at least one
          direct relationship to a neighbour in a different Louvain cluster. The backend walks every
          edge in the graph, and whenever an edge connects two nodes with different{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">component_id</code> values it tags
          both endpoints with{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">is_bridge = true</code> and records
          the set of foreign cluster IDs each bridge touches.
        </p>

        <H3 id="why-bridges-matter">Why Bridges Matter</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Bridges are the structural connectors between otherwise dense neighbourhoods — removing a
          bridge would sever its clusters from each other. In investigations they frequently
          identify:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Couriers and intermediaries</strong> moving
              between otherwise-separate rings
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Shared facilities</strong> — a warehouse, meeting
              point, or safe house used by several groups
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Shared phone numbers or accounts</strong> that
              link otherwise-unrelated networks
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          Document, Chunk, CaseDocument, and Hypothetical nodes are{' '}
          <strong className="text-foreground">excluded</strong> from bridge detection — only
          substantive entity nodes are considered. This is why the bridge count in the UI can be
          much lower than the number of raw cross-cluster edges you might see when inspecting Neo4j
          directly.
        </p>

        <p className="text-foreground mb-4 leading-relaxed">
          In the Investigate Overview panel, each bridge is rendered as a pair of entity tiles
          connected by a lightning icon with a{' '}
          <strong className="text-foreground">Show in Graph</strong> button. Clicking the button
          opens the knowledge graph focused on the first bridge endpoint so you can inspect the
          cross-cluster edge in context. The caption reads "direct edge across clusters".
        </p>

        <DocsCallout variant="tip" title="Betweenness vs Bridges">
          Both surface connector-like entities, but they answer different questions. A{' '}
          <strong className="text-foreground">bridge</strong> is a local, structural fact — it has
          at least one edge across cluster borders. High{' '}
          <strong className="text-foreground">betweenness centrality</strong> (see below) is a
          global measure — it counts how often an entity sits on shortest paths between other pairs.
          The two sets usually overlap but not always: a bridge with only one foreign edge may have
          low betweenness, and a high-betweenness hub inside a single cluster is not a bridge at
          all.
        </DocsCallout>

        <H2 id="top-entities">Top Entities by Type</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          This section ranks entities by their{' '}
          <strong className="text-foreground">Investigation Score</strong> (see above), grouped by
          entity type (Person, Organization, Location, etc.). Within each type, the top entities are
          those most central to the investigation based on the weighted composite.
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Per-type grouping</strong> ensures that important
              locations or organizations aren't overshadowed by heavily-connected persons
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Source count</strong> (
              <code className="text-xs bg-muted px-1 py-0.5 rounded">Nsrc</code>) shows how many
              documents mention the entity — more sources means stronger evidence
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Click any entity to jump to it in the knowledge graph</span>
          </li>
        </ul>

        <H2 id="betweenness-centrality">Betweenness Centrality</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Entities with high <strong className="text-foreground">betweenness centrality</strong> sit
          on many shortest paths between other entities. They are the connectors, brokers, or
          gatekeepers in the network.
        </p>

        <p className="text-foreground mb-4 leading-relaxed">
          In the UI, entities with betweenness greater than{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">0.01</code> are marked with a{' '}
          <span className="text-amber-600 dark:text-amber-500">crown icon</span>. These are bridge
          nodes — removing them would significantly disrupt communication between other parts of the
          network.
        </p>

        <DocsCallout variant="info" title="When to Focus on Bridge Nodes">
          Bridge nodes are especially important in investigations involving intermediaries — money
          launderers, facilitators, or communication hubs. Even if their investigation score is low,
          their structural position makes them valuable targets for further analysis.
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}
